/**
 * The first-paint script, as text (spec docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md,
 * "Ruling 3").
 *
 * The app sidebar renders it first (components/app-theme/app-theme-scope.tsx), during server
 * rendering, so it runs while the page is being parsed — before any app content paints. It reads the
 * hf-app-theme cookie and, for a choice the person made (Classic and Custom included) on an app page,
 * puts `data-app-theme` and the Inter variable class on <html>. No cookie — never chosen — paints
 * nothing: the page exactly as it was before themes existed. It follows the rules of app-theme-cookie.ts and app-theme-dom.ts (a spec
 * runs them all over the same cases); it is written out by hand, in plain ES5, because a function's
 * own source (`fn.toString()`) would be whatever the bundler turned it into.
 *
 * SAFETY. The script is static: the same text whatever the cookie says. It carries the catalogue as
 * data and compares the cookie against it, so only a catalogue choice can ever reach <html>; the font
 * class is embedded only when it is a plain class name; every value is JSON with `<`, `>`, `&` and
 * the line separators escaped.
 *
 * ICON SET (lib/theme/icon-sets.ts). The same script paints the hf-icon-set cookie's set, in a block of
 * its own that runs first: the theme's part returns early when there is no theme cookie, and someone
 * with an icon set and no app theme must still get it. Same safety: it carries only the sets the sheet
 * draws, so nothing else ever reaches <html>, and Classic, which is not among them, writes nothing.
 * It compares no owner: the script is static and does not know who is signed in, so a sign-in drops
 * another person's cookie instead (reconcileIconSetOwner).
 *
 * THE BRAND (./brand-theme.ts, spec docs/superpowers/specs/2026-10-29-branding-kit-and-brand-theme-design.md section
 * 3.6). `brand` is the one choice the cookie cannot paint alone: its colours are `--brand-*` variables, and the attribute
 * without them breaks every colour on the page. So for `brand`, and only for `brand`, the script reads localStorage's
 * hf-brand-tokens and puts the variables on <html> before the attribute, or writes neither. Same safety: the cache is
 * text anyone with this browser could have changed, so it is taken only when its owner is the cookie's owner and its workspace
 * is the one the API client names (an MSSP user's last client must not paint over the next), only names matching
 * --brand-(light|dark)-… and only six hex digits reach style.setProperty, and every variable the sheet reads (a list in the
 * data, never the cache's) must be among them. Storage is touched inside the `brand` branch alone, so storage that throws stops
 * no other theme, and it lands in the same catch. With the colours goes the company's sidebar mark (`data-brand-sidebar-mark`),
 * written only when it equals one of the marks the sheet draws, carried in the data; the default writes nothing.
 */
import { WORKSPACE_ID_STORAGE_KEY } from '@/lib/tenant/workspace-id-storage'
import { ICON_SET_ATTRIBUTE, ICON_SET_COOKIE, PAINTED_ICON_SETS } from '../icon-sets'
import { APP_BRAND_THEME, APP_THEME_ATTRIBUTE, APP_THEME_CHOICES, APP_THEME_COOKIE } from './app-themes'
import { APP_THEME_OFF_PATHS, isPlainClassName } from './app-theme-dom'
import {
  BRAND_SIDEBAR_MARK_ATTRIBUTE,
  BRAND_TOKENS_STORAGE_KEY,
  BRAND_VARIABLES,
  BRAND_VARIABLE_NAME,
  BRAND_VARIABLE_VALUE,
  PAINTED_BRAND_SIDEBAR_MARKS,
} from './brand-theme'

/** JSON that is also safe inside a <script> element. */
function literal(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
}

// The variables go on before the attribute, and only when every one the sheet reads is there. `k in r` never meets
// `__proto__`: JSON makes it an own key, and it does not match the name.
const BRAND_SCRIPT = [
  'if(t===a.brand.theme){',
  'c=JSON.parse(localStorage.getItem(a.brand.key));',
  'if(v.length!==2||!c||c.owner!==v[1]||(c.workspace||null)!==localStorage.getItem(a.brand.workspace)||!c.vars||typeof c.vars!=="object")return;',
  'r={};',
  `for(k in c.vars)if(${BRAND_VARIABLE_NAME}.test(k)&&typeof c.vars[k]==="string"&&${BRAND_VARIABLE_VALUE}.test(c.vars[k]))r[k]=c.vars[k];`,
  'for(i=0;i!==a.brand.need.length;i++)if(!r[a.brand.need[i]])return;',
  'for(k in r)d.style.setProperty(k,r[k]);',
  // The sidebar mark goes with the colours: only one the sheet draws, compared against the data, never the cache's text.
  'for(i=0;i!==a.brand.marks.length;i++)if(a.brand.marks[i]===c.mark)d.setAttribute(a.brand.markAttr,c.mark);',
  '}',
].join('')

const SCRIPT = [
  'try{',
  'var p=location.pathname,i,s,m,v,t,d,c,k,r;',
  // The play area, the portal and the console: never, whatever the cookie says.
  'for(i=0;i!==a.off.length;i++){s=a.off[i];if(p===s||p.indexOf(s+"/")===0)return;}',
  String.raw`m=new RegExp("(?:^|;\\s*)"+a.name+"=([^;]*)").exec(document.cookie);`,
  'if(!m)return;',
  // <theme>[.<owner>] — the same shape parseAppThemeCookieValue accepts, nothing else.
  'v=m[1].split(".");',
  'if(v.length>2)return;',
  't=v[0];',
  'for(i=0;i!==a.themes.length;i++)if(a.themes[i]===t)break;',
  'if(i===a.themes.length)return;',
  String.raw`if(v.length===2&&!/^[A-Za-z0-9-]{1,64}$/.test(v[1]))return;`,
  'd=document.documentElement;',
  BRAND_SCRIPT,
  'd.setAttribute(a.attr,t);',
  'if(a.font)d.classList.add(a.font);',
  '}catch(e){}',
].join('')

const ICON_SET_SCRIPT = [
  'try{',
  'var b=a.icons,p=location.pathname,i,s,m,v;',
  // The play area, the portal and the console: never, whatever the cookie says.
  'for(i=0;i!==a.off.length;i++){s=a.off[i];if(p===s||p.indexOf(s+"/")===0)return;}',
  String.raw`m=new RegExp("(?:^|;\\s*)"+b.name+"=([^;]*)").exec(document.cookie);`,
  'if(!m)return;',
  // <set>[.<owner>] — the same shape parseIconSetCookieValue accepts, nothing else.
  'v=m[1].split(".");',
  'if(v.length>2)return;',
  String.raw`if(v.length===2&&!/^[A-Za-z0-9-]{1,64}$/.test(v[1]))return;`,
  'for(i=0;i!==b.sets.length;i++)if(b.sets[i]===v[0])document.documentElement.setAttribute(b.attr,v[0]);',
  '}catch(e){}',
].join('')

/** The script's source. `fontClass` is dropped unless it is a plain class name. */
export function appThemeBootScript(fontClass?: string): string {
  const args = {
    name: APP_THEME_COOKIE,
    attr: APP_THEME_ATTRIBUTE,
    themes: [...APP_THEME_CHOICES],
    off: [...APP_THEME_OFF_PATHS],
    font: isPlainClassName(fontClass) ? fontClass : null,
    icons: { name: ICON_SET_COOKIE, attr: ICON_SET_ATTRIBUTE, sets: [...PAINTED_ICON_SETS] },
    brand: {
      theme: APP_BRAND_THEME,
      key: BRAND_TOKENS_STORAGE_KEY,
      workspace: WORKSPACE_ID_STORAGE_KEY,
      need: [...BRAND_VARIABLES],
      marks: [...PAINTED_BRAND_SIDEBAR_MARKS],
      markAttr: BRAND_SIDEBAR_MARK_ATTRIBUTE,
    },
  }
  return `(function(a){(function(){${ICON_SET_SCRIPT}})();${SCRIPT}})(${literal(args)})`
}
